import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const AuthContext = createContext(null);

const ROLE_LABELS = {
  superadmin_platform: 'Super Admin',
  admin: 'Administrateur',
  superviseur: 'Superviseur',
  responsable: 'Responsable',
  accueil: 'Accueil',
  consultation: 'Consultation',
};

function decodeJWT(token) {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
}

// Construit l'objet `user` unique consommé partout dans l'app (Navbar,
// Sidebar, pages) à partir des champs JWT bruts — calcule `display_role`
// une seule fois ici : le nom du/des groupe(s) remplace le rôle système dès
// qu'un groupe est assigné (sauf mode 'role_only'), pour ne plus jamais
// afficher "Consultation" à un utilisateur dont le groupe domine ses droits.
function buildUser(payload) {
  const groups = payload.groups || [];
  const permissionMode = payload.permission_mode || 'group_only';
  const isSuperAdmin = payload.role === 'superadmin_platform';
  const displayRole = (!isSuperAdmin && permissionMode !== 'role_only' && groups.length > 0)
    ? groups.join(' + ')
    : (ROLE_LABELS[payload.role] || payload.role);

  return {
    id: payload.user_id,
    username: payload.username,
    full_name: payload.full_name,
    role: payload.role,
    church_id: payload.church_id || null,
    church_name: payload.church_name || null,
    permissions: payload.permissions || [],
    permission_mode: permissionMode,
    groups,
    display_role: displayRole,
  };
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Restore session from localStorage on mount
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (token) {
      const decoded = decodeJWT(token);
      if (decoded && decoded.exp * 1000 > Date.now()) {
        setUser(buildUser(decoded));
      } else {
        // Token expired, try refresh
        const refreshToken = localStorage.getItem('refresh_token');
        if (refreshToken) {
          axios
            .post(`${API_BASE_URL}/token/refresh/`, { refresh: refreshToken })
            .then((res) => {
              localStorage.setItem('access_token', res.data.access);
              const newDecoded = decodeJWT(res.data.access);
              if (newDecoded) setUser(buildUser(newDecoded));
            })
            .catch(() => {
              localStorage.clear();
            })
            .finally(() => setLoading(false));
          return;
        } else {
          localStorage.clear();
        }
      }
    }
    setLoading(false);
  }, []);

  const login = useCallback(async (username, password) => {
    const res = await axios.post(`${API_BASE_URL}/auth/login/`, { username, password });
    const { access, refresh } = res.data;
    localStorage.setItem('access_token', access);
    localStorage.setItem('refresh_token', refresh);
    setUser(buildUser(res.data));
    return res.data;
  }, []);

  const logout = useCallback(() => {
    localStorage.clear();
    setUser(null);
  }, []);

  const value = { user, loading, login, logout };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

export default AuthContext;
